/**
 * Status Badges
 *
 * Header badges showing active capabilities; uses design tokens for consistent theme.
 */

import React from 'react'
import { Mic, Video, Monitor, Zap } from 'lucide-react'
import { TEXT_SIZE, TEXT, GAP, SCALE } from './chat/design-tokens'
import { Tooltip } from './chat/UIHelpers'
import { ModalityLocationBadge } from './chat/ModalityLocationBadge'
import type { ModalityStateProps } from './chat/modality-types'

interface CapabilityChipProps {
  icon: React.ReactNode
  label: string
  tooltip: string
  capabilityButtonClass: string
  capabilityTextClass: string
}

const CapabilityChip: React.FC<CapabilityChipProps> = ({
  icon,
  label,
  tooltip,
  capabilityButtonClass,
  capabilityTextClass,
}) => (
  <Tooltip text={tooltip}>
    <div className={capabilityButtonClass}>
      {icon}
      <span className={capabilityTextClass}>{label}</span>
    </div>
  </Tooltip>
)

export interface StatusBadgesProps extends ModalityStateProps {
  isProcessing?: boolean | undefined
  className?: string | undefined
}

const StatusBadges: React.FC<StatusBadgesProps> = ({
  isVoiceActive,
  isWebcamActive,
  isScreenShareActive,
  isLocationShared,
  isProcessing: _isProcessing,
  className = '',
  locationData,
}) => {
  const activeBadges = [
    isVoiceActive,
    isWebcamActive,
    isScreenShareActive,
    isLocationShared,
  ].filter(Boolean).length

  if (activeBadges === 0 && !_isProcessing) return null

  const capabilityButtonClass =
    'inline-flex items-center gap-1.5 px-2 py-1 border border-border/60 bg-secondary/35 hover:bg-secondary/60 transition-colors duration-150'
  const capabilityTextClass = `${TEXT_SIZE.xs} font-mono font-semibold tracking-[0.14em] uppercase ${TEXT.secondary}`

  return (
    <div className={`flex items-center ${GAP.sm} ${className}`} data-testid="status-badges">
      <div className="flex items-center gap-1">
        {isVoiceActive && (
          <CapabilityChip
            tooltip="Voice input active"
            label="Voice"
            icon={<Mic className={`${SCALE.sm} ${TEXT.primary}`} />}
            capabilityButtonClass={capabilityButtonClass}
            capabilityTextClass={capabilityTextClass}
          />
        )}
        {isWebcamActive && (
          <CapabilityChip
            tooltip="Webcam shared"
            label="Video"
            icon={<Video className={`${SCALE.sm} ${TEXT.primary}`} />}
            capabilityButtonClass={capabilityButtonClass}
            capabilityTextClass={capabilityTextClass}
          />
        )}
        {isScreenShareActive && (
          <CapabilityChip
            tooltip="Screen shared"
            label="Screen"
            icon={<Monitor className={`${SCALE.sm} ${TEXT.primary}`} />}
            capabilityButtonClass={capabilityButtonClass}
            capabilityTextClass={capabilityTextClass}
          />
        )}
        {isLocationShared && (
          <ModalityLocationBadge
            locationData={locationData}
            tooltipText={locationData?.city || 'Location'}
            ariaLabel="Show location coordinates"
            precision={6}
            trigger={
              <div className={capabilityButtonClass}>
                <span
                  className={`${SCALE.sm} ${TEXT.primary} inline-flex items-center justify-center`}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path d="M12 21s-6-4.35-6-10a6 6 0 1 1 12 0c0 5.65-6 10-6 10Z" />
                    <circle cx="12" cy="11" r="2.5" />
                  </svg>
                </span>
                <span className={capabilityTextClass}>{locationData?.city || 'Location'}</span>
              </div>
            }
          />
        )}
      </div>
      {_isProcessing && (
        <CapabilityChip
          tooltip="F.B/c is working"
          label="Thinking"
          icon={<Zap className={`${SCALE.sm} ${TEXT.primary}`} />}
          capabilityButtonClass={capabilityButtonClass}
          capabilityTextClass={capabilityTextClass}
        />
      )}
    </div>
  )
}

export const ConnectionStatus: React.FC<{
  connected: boolean
  latency?: number | undefined
  className?: string
  offlineLabel?: 'Text only' | 'Offline'
}> = ({ connected, latency, className = '', offlineLabel = 'Text only' }) => {
  const label = connected ? (latency != null ? `Active (${latency}ms)` : 'Active') : offlineLabel
  return (
    <Tooltip text={label}>
      <div
        role="img"
        className={`
          h-2 w-4 rounded-none shrink-0 border border-foreground/20
          ${
            connected
              ? 'bg-foreground/90 border-foreground/70'
              : 'bg-transparent border-foreground/18'
          }
          ${className}
        `}
        aria-label={label}
      />
    </Tooltip>
  )
}

export default StatusBadges
